<template>
  <div>
    <w-nav-links />
    <w-header />
    <main>
      <router-view v-slot="{ Component }">
        <transition name="book-flip" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <transition name="nav-right">
      <w-nav v-if="isNavShown" />
    </transition>
  </div>
</template>

<script>
import WHeader from "./components/WHeader";
import WNav from "./components/WNav";
import WNavLinks from "./components/WNavLinks.vue";

export default {
  components: { WNav, WNavLinks, WHeader },
  watch: {
    $route() {
      this.$store.commit("toggleNav", false);
    },
  },
  computed: {
    isNavShown() {
      return this.$store.state.isNavShown;
    },
    thePartner() {
      return this.$store.state.partner;
    },
    thisUser() {
      return this.$store.state.user;
    },
    socket() {
      return this.$store.state.socket;
    },
  },
  mounted() {
    // apply the user's preferred settings
    if (localStorage.getItem("theme") === "dark") {
      document.body.classList.add("dark-mode");
    }

    if (localStorage.getItem("language")) {
      this.$store.commit("changeLang", localStorage.getItem("language"));
    }

    // delete the data from the previous version
    if (localStorage.getItem("thisUser")) {
      localStorage.removeItem("thisUser");
    }
    if (localStorage.getItem("currentUser")) {
      localStorage.removeItem("currentUser");
    }
  },
};
</script>

<style lang="scss">
* {
  box-sizing: border-box;
  transition: background-color 0.2s linear;
  -webkit-tap-highlight-color: transparent;
  scroll-behavior: smooth;
  font-family: poppins, cairo, Avenir, Helvetica, Arial, sans-serif;
}
html[lang="ar"] {
  #app,
  h3 {
    direction: rtl;
  }
  .rules ul {
    text-align: right !important;
  }
  .login-page {
    label {
      text-align: right !important;
    }
  }
}
body {
  margin: 0;
  width: 100vw;
  min-height: 100vh;
  overflow-x: hidden;
  --text: rgb(31, 31, 31);
  --text-contrast: white;
  --accent: rgb(103, 55, 216);
  --bg: #f0f0f0;
  --bg2: #fcfcfc;
  --tent: rgba(103, 55, 216, 0.1);
  --tent-strong: rgba(103, 55, 216, 0.3);
  --shadow: rgba(0, 0, 0, 0.2);
  --theme-button-flex: flex-start;
  --theme-button-flex-contrast: flex-end;
  --chat-message-background: rgb(42, 145, 97);
  --chat-reply-background: rgba(42, 145, 97, 0.3);
  --chat-reply-color: rgb(42, 145, 97);
  background-color: var(--bg);

  &.dark-mode {
    --text: rgb(255, 255, 255);
    --text-contrast: black;
    --bg: #080c1d;
    --bg2: #0c122c;
    --theme-button-flex: flex-end;
    --theme-button-flex-contrast: flex-start;

    .ad-space img {
      filter: brightness(60%);
    }

    .themes {
      button {
        i {
          &.switch {
            left: auto !important;
            right: 6px;
          }
          &:not(.switch) {
            right: auto !important;
            left: 6px;
          }
        }
      }
    }
  }
}

a {
  color: var(--accent);
  text-decoration: none;
}

.admin-badge {
  margin: 0 4px 0 0;
  font-style: normal;
  &::before {
    content: "\f3ed";
    font-weight: 900;
    font-family: "Font Awesome 5 Free", poppins, cairo;
  }
}

input {
  margin: 0 10px;
  transform: translateY(2px);
  accent-color: var(--accent);
  &[type="checkbox"],
  &[type="radio"] {
    cursor: pointer;
  }
}

select {
  width: 100%;
  padding: 16px;
  margin: 10px 0 0;
  border: none;
  outline: none;
  background-color: var(--tent);
  color: var(--text);
  border-radius: 4px;

  option {
    background-color: var(--bg);
  }
}

button {
  border: none;
  outline: none;
  cursor: pointer;
  color: var(--text);
  font-weight: bold;
  transition: transform 0.2s linear;
  background-color: transparent;
  &:not(header button, nav button, .close) {
    color: white;
    width: fit-content;
    background-color: var(--accent);
    padding: 10px 20px;
    margin: 10px 0;
    border-radius: 30px;
  }
  &:hover {
    transform: scale(1.08);
  }
}

#app > div {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text);
  height: 100vh;
  margin: 0 auto;
  position: relative;

  display: flex;
  flex-flow: column nowrap;

  main {
    height: calc(100% - 80px);
    text-align: center;
    .page {
      height: 100%;
      padding: 0 4%;
      overflow: auto;
      p {
        width: 90%;
        margin: 20px auto;
      }
    }
  }
}

.nav-right-enter-from,
.nav-right-leave-to {
  transform: translateX(100%);
}
.nav-right-enter-active,
.nav-right-leave-active {
  transition: transform 0.4s ease-out;
}

.book-flip-enter-from,
.book-flip-leave-to {
  transform: rotateY(40deg);
  opacity: 0;
}
.book-flip-enter-active,
.book-flip-leave-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}

.opacity-enter-from,
.opacity-leave-to {
  opacity: 0;
}
.opacity-enter-active,
.opacity-leave-active {
  transition: opacity 0.2s ease-out;
}

.from-bottom-enter-from,
.from-bottom-leave-to {
  transform: translateY(100%);
}
.from-bottom-enter-active,
.from-bottom-leave-active {
  transition: transform 0.2s ease-out;
}
::-webkit-scrollbar {
  width: 10px;
}

@media (max-width: 499px) {
  ::-webkit-scrollbar {
    width: 0;
  }
}

textarea::-webkit-scrollbar {
  width: 0px;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

::-webkit-scrollbar-thumb {
  // border-radius: 8px;
  background: rgba(103, 55, 216, 0.6);
  // box-shadow: inset 0 0 6px rgba(255, 255, 255, 0.8);
}
</style>
